import { createUuid } from '@notifi/uuid/browser';
import { isComplimentaryBillingOption } from '@/lib/membership/complimentary-option';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { isMembershipLine } from '@notifi/shared-types/membership-line';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Search, Loader2, Plus } from 'lucide-react';
import { Input } from '@/components/atoms/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/atoms/popover';
import { Button } from '@/components/atoms/button';
import { operationsClient, apiPost, apiGet, operationsPricebookEndpoints } from '@/lib/api';
import { getActiveTenantQueryKey } from '@/lib/activeTenant';
import {
  formatMembershipPlanSummary,
  type MembershipPlanSummary,
} from '@/lib/membership/format-plan-summary';
import { membershipPlanKeys } from '@/lib/queryKeys';
import { partPricingTreatmentForService, type PricingTreatment } from '@/lib/sales/service-rollup';
import { canHoldVisibilityDecision, defaultChargeableForType } from '@/lib/sales/line-economics';
import {
  normalizeDisplayConfig,
  type DisplayConfig,
  type PricebookConfiguration,
} from '@/types/pricebook';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { useAllEquipment } from '@/hooks/equipment/useEquipment';
import { priceMembershipOptionForEquipment } from '@/lib/membership/home-sale-pricing';
import {
  useMembershipPlans,
  membershipPlansFromQueryData,
} from '@/hooks/pricebook/useMembershipPlans';
import { MembershipPricingBreakdown } from '@/components/organisms/pricebook/MembershipPricingBreakdown';
import {
  ConfigurableVariantDialog,
  type ConfigurableVariant,
} from '@/components/organisms/pricebook/ConfigurableVariantDialog';
import { toMemberPricingItemType } from '@/components/organisms/pricebook/support/membership-pricing-breakdown';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';
import {
  billingIntervalForOption,
  type MembershipBillingOption,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import { MembershipBillingOptionDialog } from '@/components/organisms/pricebook/MembershipBillingOptionDialog';
import {
  usePlanRecurringServices,
  type PlanRecurringService,
} from '@/hooks/membership-plans/usePlanRecurringServices';
import type {
  DynamicPriceBreakdown,
  LaborRateProfile,
  MaterializedServicePart,
  ServiceLaborLine,
} from '@/types/pricebook';
import { useLaborRateProfiles } from '@/hooks/pricebook/useDynamicPricing';
import { freezeMarginShares } from '@/lib/sales/service-labor';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PricebookLineItem {
  id: string;
  lineItemId?: string;
  itemId?: string;
  itemVersion?: number;
  name: string;
  code: string;
  type: string;
  unit?: string | null;
  description?: string;
  basePrice?: number | string | null;
  laborRate?: number | string | null;
  /** SERVICE only — what an hour COSTS us. See `PricebookItem.laborCostRate`. */
  laborCostRate?: number | string | null;
  /** SERVICE only — default hours the catalog expects the work to take. */
  estimatedHours?: number | string | null;
  /** SERVICE only — see `PricebookItem.laborCostLines`. */
  laborCostLines?: ServiceLaborLine[] | null;
  costBasis?: number;
  markupPercent?: number;
  /** On a resolved SERVICE COMPONENT: the relationship's own pricing answers. */
  pricingTreatment?: PricingTreatment | null;
  /**
   * On a resolved SERVICE COMPONENT: the pricebook's default for whether the
   * customer sees this part named. Read as a VETO only — see
   * `visibleToCustomerFlag`.
   */
  showInDetail?: boolean | null;
  markupOverride?: number | null;
  memberPrice?: number | string | null;
  memberDiscountPercent?: number | string | null;
  currency?: string;
  priceStrategy?: string | null;
  taxable?: boolean | null;
  taxCategory?: string | null;
  taxCode?: string | null;
  taxLineType?: string | null;
  glAccount?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  glClass?: string | null;
  status?: string | null;
  displayConfig?: DisplayConfig | null;
  componentDisplayMode?: 'ITEMIZED' | 'ROLLED_UP' | null;
  membershipConfig?: { planId?: string | null } | null;
  membershipPlanIds?: string[];
  upsellItems?: Array<{ itemId?: string | null; relationshipType?: string | null }>;
  isConfigurable?: boolean;
  configurations?: PricebookConfiguration[];
  dynamicPricingConfig?: import('@/types/pricebook').DynamicServicePricingConfig;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
  __requiresDetailFetch?: boolean;
  /** Internal marker: this part belongs to a service inside a bundle. */
}

export interface PickedLineItem {
  lineItemId: string;
  itemVersion: number;
  name: string;
  code: string;
  type: string;
  unit: string;
  description: string;
  basePrice: number;
  quantity: number;
  taxable: boolean;
  /** Non-taxable pricebook default retained for estimate-only override UX. */
  pricebookTaxable?: boolean;
  priceStrategy: string;
  taxLineType: string | null;
  /**
   * How the tax engine classifies this line, and where its money posts. All
   * five are the CATALOG's answers, snapshotted onto the document the same way
   * the price is: the pricebook is where they are decided, and a line that
   * dropped them re-derived a tax class from its bare type and posted to no
   * account at all.
   */
  taxCategory?: string | null;
  taxCode?: string | null;
  glAccount?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  glClass?: string | null;
  laborRate: number;
  /**
   * What an hour of this service COSTS us — never what it sells for. Drives the
   * Labor figure on the cost panel on a FLAT service as well as a dynamic one.
   */
  laborCostRate?: number;
  costBasis?: number;
  markupPercent?: number;
  /**
   * MATERIAL / EQUIPMENT: whether THIS line bills at its own price. A part
   * added to a document starts unbilled because it is a cost of performing the
   * work until the operator explicitly chooses to sell it separately.
   */
  chargeable?: boolean;
  /**
   * How the SERVICE carrying this part charges for it, when it arrives as a
   * component. Null leaves the line's own `chargeable` as the default.
   */
  pricingTreatment?: PricingTreatment | null;
  /**
   * Snapshotted from the service component's `showInDetail`: the operator hid
   * this part on the SERVICE, so the line it becomes starts hidden too.
   *
   * Set only when the pricebook actually says "hide". Visibility has no
   * positive default to snapshot — an explicit `true` on every component would
   * drag consumables the customer has never been shown onto their document.
   */
  hiddenFromCustomer?: boolean;
  /** Markup percentage this relationship uses instead of the item's own. */
  markupOverride?: number | null;
  /** NOT-605 — item-level member pricing carried to the estimate line. */
  memberPrice?: number | null;
  memberDiscountPercent?: number | null;
  estimatedHours?: number;
  /**
   * A SERVICE's labor rows, with each margin share's percentage frozen on the
   * way out of the picker (`freezeMarginShares`). The hourly rows are already
   * reduced into `estimatedHours` / `laborCostRate`; a share is recorded
   * nowhere else, so a line that dropped the rows costed its labor at $0.
   */
  laborCostLines?: ServiceLaborLine[];
  dynamicPricingConfig?: import('@/types/pricebook').DynamicServicePricingConfig;
  displayConfig?: DisplayConfig | null;
  membershipPlanId?: string;
  membershipSetupFee?: number;
  membershipSetupFeeFor?: string;
  membershipSetupFeePlanId?: string;
  membershipBillingOptionId?: string;
  membershipCustomerChoosesBillingOption?: boolean;
  membershipPlanSummary?: MembershipPlanSummary;
  /** Physical customer units selected for a service/membership line. */
  equipmentIds?: string[];
  equipmentLocationOverrideReason?: string;
  isCustom?: boolean;
  /**
   * NOT-590 — stable identity for this line, so a service and the component
   * lines it produced can reference each other. Positional index was never safe
   * for that: reordering or deleting a line would re-parent another service's
   * components.
   */
  lineKey?: string;
  /** On a component line: the `lineKey` of the SERVICE that includes it. */
  parentLineKey?: string;
  /** On a SERVICE line with components: how the CUSTOMER sees the breakdown. */
  componentDisplayMode?: 'ITEMIZED' | 'ROLLED_UP';
  /** An optional component lands unselected: offered, not sold. */
  isOptional?: boolean;
  isSelected?: boolean;
  /**
   * Set when this line came from ONE configuration of a configurable equipment
   * item. `lineItemId` still points at the configurable catalog item (that's the
   * attribution/pricing source), so this name is what makes the line a resolved
   * selection rather than an unresolved placeholder — the sales APIs require it
   * on any line whose catalog item is configurable, and it discriminates two
   * configurations of the same parent in the frontend's per-row dedupe.
   */
  configurationName?: string;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
  /** NOT-628 — the plan's first included visit was delivered with the sale. */
  membershipDeliveredServiceIds?: string[];
  /**
   * A price this line must keep, whatever the catalog says. Used for a visit
   * the membership already covers: it belongs on the document the customer
   * signs, at no charge.
   */
  manualPriceOverride?: number | null;
}

export interface LineItemPickerAddMeta {
  source: 'pricebook' | 'custom';
}

export interface MembershipLineItemEditRequest {
  requestId: number;
  rowId: string;
  item: PickedLineItem;
}

export interface MembershipLineItemAddRequest {
  requestId: number;
  planId: string;
}

interface LineItemPickerProps {
  /** Optional document scope; shared catalog items remain available. */
  businessUnitId?: string | null;
  onAdd: (item: PickedLineItem, meta?: LineItemPickerAddMeta) => void;
  excludeIds?: string[];
  placeholder?: string;
  filterItem?: (item: PricebookLineItem) => boolean;
  emptyMessage?: string;
  allowedTypes?: string[];
  allowCustomItems?: boolean;
  hidePrices?: boolean;
  membershipCustomerId?: string | null;
  /**
   * Whether this document can carry "the tech already performed this visit".
   * Off unless the surface actually persists the answer — a question asked
   * where nothing reads it is worse than no question, and on a shared template
   * it would freeze one customer's visit into every document built from it.
   */
  allowDeliveredVisits?: boolean;
  membershipServiceAddressId?: string | null;
  /** Savings this plan would add to the lines already on the document. */
  getMembershipPotentialSavings?: (plan: MembershipPlan) => number;
  /**
   * NOT-590 — when true, picking a SERVICE that includes materials/equipment
   * calls `onAdd` once for the service and once per component, so the document
   * ends up with real priced lines for each.
   *
   * Opt-in per surface: a picker used to choose ONE item (a production kit's
   * service, a job type's default) must keep returning exactly one line.
   */
  expandComponents?: boolean;
  /**
   * When true, MATERIAL or EQUIPMENT picked on its own starts NOT chargeable —
   * a part is a cost used to perform the work unless the operator explicitly
   * chooses to sell it as a separate line.
   *
   * OPT-IN, and the condition is strict: only a surface that renders the
   * Chargeable control (`updateChargeable` on `EstimateLineItemsSection`) may
   * set it. An unbilled line hides its price and markup fields, so switching
   * this on anywhere the control is absent would draw a part as "Not billed"
   * with no way to price it and no way back. Estimate/proposal documents and
   * their template editors are those surfaces — they share one picker set
   * (`documentLinePickers`) precisely so they cannot diverge. A job's
   * financials tab renders the tick on every part row and opts in too; the
   * job form and job types render no tick and keep every picked line billed.
   *
   * Only for a line picked STANDALONE. A part arriving through a service — an
   * expanded component, or one attached with the component picker — is answered
   * by the relationship's `pricingTreatment`, which outranks this either way.
   */
  partsStartUnbilled?: boolean;
  // NOT-530 — when the picker lives inside a modal Sheet/Dialog, portal the
  // results popover into a node within that dialog so its wheel-scroll isn't
  // swallowed by the dialog's scroll-lock. Defaults to document.body.
  portalContainer?: HTMLElement | null;
  /** Reopen the membership configurator for an existing document line. */
  membershipEditRequest?: MembershipLineItemEditRequest | null;
  /** Open the configurator for a recommended plan that is not on the document yet. */
  membershipAddRequest?: MembershipLineItemAddRequest | null;
  onEditMembership?: (item: PickedLineItem) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A stable per-line id used to tie a service to the component lines it produced.
 * The shared browser generator handles WebViews without native randomUUID.
 */
function createLineKey(): string {
  return createUuid();
}

function resolveId(item: PricebookLineItem): string {
  return item.id ?? item.lineItemId ?? item.itemId ?? '';
}

function safeNumber(value: number | string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function normalize(raw: PricebookLineItem): PricebookLineItem {
  return {
    ...raw,
    id: resolveId(raw),
    basePrice: safeNumber(raw.basePrice),
    laborRate: safeNumber(raw.laborRate),
    laborCostRate: safeNumber(raw.laborCostRate),
    estimatedHours: safeNumber(raw.estimatedHours),
    costBasis: safeNumber(raw.costBasis),
    markupPercent: safeNumber(raw.markupPercent),
    unit: raw.unit || 'each',
    priceStrategy: raw.priceStrategy ?? 'FLAT',
    // Default to taxable when the catalog flag is absent (matches Prisma @default(true)
    // and the backend); an explicit `false` is still respected.
    taxable: raw.taxable ?? true,
    taxCategory: raw.taxCategory ?? null,
    taxCode: raw.taxCode ?? null,
    taxLineType: raw.taxLineType ?? null,
    glAccount: raw.glAccount ?? null,
    glCogs: raw.glCogs ?? null,
    glExpenseAccount: raw.glExpenseAccount ?? null,
    glClass: raw.glClass ?? null,
    itemVersion: raw.itemVersion ?? 1,
    displayConfig: normalizeDisplayConfig(raw.displayConfig),
  };
}

function hasOwnField(item: PricebookLineItem, field: keyof PricebookLineItem): boolean {
  return Object.prototype.hasOwnProperty.call(item, field);
}

function hasCompletePickerPayload(raw: PricebookLineItem): boolean {
  if (isMembershipType(raw.type) && !raw.membershipConfig && !raw.membershipPlanIds?.length) {
    return false;
  }

  return (
    hasOwnField(raw, 'itemVersion') &&
    hasOwnField(raw, 'description') &&
    hasOwnField(raw, 'unit') &&
    raw.basePrice != null &&
    raw.laborRate != null &&
    raw.costBasis != null &&
    raw.markupPercent != null &&
    raw.priceStrategy != null &&
    raw.taxable != null &&
    hasOwnField(raw, 'taxLineType') &&
    hasOwnField(raw, 'displayConfig') &&
    // The tax class and accounts are snapshotted onto the document too, so a
    // payload that omits them is not complete enough to add — the line would
    // be saved posting to no account while the catalog knew better.
    //
    // `hasOwnField`, not a null check: null is a real answer here (the item
    // genuinely maps to no account), and requiring a value would send every
    // unmapped item through a detail fetch that returns the same null.
    hasOwnField(raw, 'taxCategory') &&
    hasOwnField(raw, 'taxCode') &&
    hasOwnField(raw, 'glAccount') &&
    hasOwnField(raw, 'glCogs') &&
    hasOwnField(raw, 'glExpenseAccount') &&
    hasOwnField(raw, 'glClass')
  );
}

function normalizeSearchResult(raw: PricebookLineItem): PricebookLineItem {
  return {
    ...normalize(raw),
    __requiresDetailFetch: !hasCompletePickerPayload(raw),
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function isTimeAndMaterialsCatalogItem(item: PricebookLineItem): boolean {
  return (
    String(item.type ?? '').toUpperCase() === 'SERVICE' &&
    String(item.priceStrategy ?? '').toUpperCase() === 'DYNAMIC'
  );
}

/**
 * What a catalog item sells for, as the picker lists it.
 *
 * `basePrice` for everything, INCLUDING a dynamic service. This used to read a
 * dynamic service's `laborRate` and print it as "/hr" — but the pricebook write
 * path stores that column as 0 for a dynamic service (its time lives in
 * `laborLines`, at possibly several rates), so every dynamic service listed as
 * "$0.00/hr". A dynamic service has a calculated TOTAL, materialized onto
 * `basePrice`, and that is the number to show.
 */
function catalogDisplayPrice(item: PricebookLineItem): number {
  return safeNumber(item.basePrice);
}

function catalogDisplayPriceText(item: PricebookLineItem): string {
  // A dynamic service that has never been priced has nothing to state. Saying
  // "$0.00" would read as free, which is worse than admitting the gap.
  if (isTimeAndMaterialsCatalogItem(item) && catalogDisplayPrice(item) <= 0) {
    return 'Price not set';
  }
  return formatCurrency(catalogDisplayPrice(item));
}

function isMembershipType(type: string | null | undefined): boolean {
  return String(type ?? '').toUpperCase() === 'MEMBERSHIP';
}

function isServiceType(type: string | null | undefined): boolean {
  return String(type ?? '').toUpperCase() === 'SERVICE';
}

function isBundleType(type: string | null | undefined): boolean {
  return String(type ?? '').toUpperCase() === 'BUNDLE';
}

/**
 * The catalog rows an item carries, as the components endpoint resolves them.
 *
 * The endpoint returns `{ data, warnings }` rather than a bare array; both
 * shapes are accepted because older responses were the array. Throws on a
 * failed request — every caller has its own answer to what that means.
 */
async function fetchComponents(itemId: string): Promise<PricebookLineItem[]> {
  const raw = await apiGet<PricebookLineItem[] | { data?: PricebookLineItem[] }>(
    operationsClient,
    operationsPricebookEndpoints.components(itemId),
  );
  return Array.isArray(raw) ? raw : (raw?.data ?? []);
}

/**
 * How many of a component the thing above it holds.
 *
 * Absent means one — a component row without a quantity is one of that thing,
 * not none of it. Negative is coerced away rather than trusted: a catalog row
 * cannot hold a negative count, and letting one through would subtract from
 * the bundle's cost.
 *
 * Read through a cast because `quantity` belongs to the COMPONENT row rather
 * than to the catalog item, and `PricebookLineItem` — what both callers hold —
 * does not declare it.
 */
function memberQuantity(member: unknown): number {
  const raw = (member as { quantity?: number } | undefined)?.quantity;
  return raw == null ? 1 : Math.max(0, safeNumber(raw));
}

/** MATERIAL and EQUIPMENT are the two that can be sold inside something else. */
function isPartType(type: string | null | undefined): boolean {
  const normalized = String(type ?? '').toUpperCase();
  return normalized === 'MATERIAL' || normalized === 'EQUIPMENT';
}

/**
 * WHAT THE CALCULATED PRICE IS MADE OF, frozen onto the document line.
 *
 * The catalog's own breakdown — the parts, their quantities and what each one
 * contributed — recorded so the service can describe itself without reading the
 * part lines it was expanded into. Those lines are the operator's: repricing one
 * in the Materials section says nothing about what the service was configured to
 * contain, and letting it say so is the leak this snapshot exists to close.
 *
 * The per-unit contribution is what is stored, so changing the configured
 * quantity scales it the way the pricebook's formula does.
 *
 * `components` — the resolved catalog rows — are optional because they are
 * fetched on the expansion path only. They supply the part TYPE, which the
 * pricing breakdown does not carry and which decides whether an uplift charged
 * on equipment alone reaches this part.
 */
function materializedPartsFrom(
  preview: DynamicPriceBreakdown,
  components?: PricebookLineItem[],
): MaterializedServicePart[] {
  const typeById = new Map(
    (components ?? []).map((component) => [resolveId(component), component.type]),
  );
  return (preview.components ?? []).map((component) => ({
    itemId: component.itemId,
    name: component.name,
    type: typeById.get(component.itemId),
    quantity: component.quantity,
    // The breakdown states the total; one of them is what the configuration is
    // built from, and `component.unitPrice` is exactly that.
    unitAmount: component.unitPrice,
  }));
}

function membershipPlanIdForItem(item: PricebookLineItem | undefined): string | undefined {
  return item?.membershipConfig?.planId || item?.membershipPlanIds?.[0] || undefined;
}

function effectiveBillingOptions(plan: MembershipPlan): MembershipBillingOption[] {
  // Each option charges the price it was authored with — including an upfront
  // term, whose price IS the reward for paying ahead.
  return plan.billingOptions?.length ? plan.billingOptions : [];
}

function unwrapLineItemDetail(
  data: PricebookLineItem | { data?: PricebookLineItem } | undefined,
): PricebookLineItem | undefined {
  if (!data) return undefined;
  if (Object.prototype.hasOwnProperty.call(data, 'data')) {
    return (data as { data?: PricebookLineItem }).data;
  }
  return data as PricebookLineItem;
}

function unwrapMembershipPlan(
  data: MembershipPlan | { data?: MembershipPlan } | undefined,
): MembershipPlan | undefined {
  if (!data) return undefined;
  if (Object.prototype.hasOwnProperty.call(data, 'data')) {
    return (data as { data?: MembershipPlan }).data;
  }
  return data as MembershipPlan;
}

function lineItemPickerDetailKey(id: string) {
  return ['lineItemPicker', getActiveTenantQueryKey(), 'lineItemDetail', id] as const;
}

function restatedMemberPrice(
  memberPrice: number | string | null | undefined,
  previousPrice: number,
  nextPrice: number,
): number | null {
  if (memberPrice == null) return null;
  const member = Number(memberPrice);
  if (!Number.isFinite(member) || !(previousPrice > 0)) return null;
  return Math.round(member * (nextPrice / previousPrice) * 100) / 100;
}

function toPickedLineItem(
  source: PricebookLineItem,
  membershipPlan?: MembershipPlan,
  billingOption?: MembershipBillingOption,
  configurationName?: string,
  partsStartUnbilled = false,
  membershipSaleEquipment:
    | Array<{
        id: string;
        category: string;
        name?: string | null;
        serialNumber?: string | null;
      }>
    | undefined = undefined,
  /** Included visits the tech performed on the same trip as the sale. */
  membershipDeliveredServiceIds: string[] = [],
  customerChoosesBillingOption = false,
): PickedLineItem {
  const isMembership = isMembershipLine(source);
  const interval = billingOption ? billingIntervalForOption(billingOption) : null;
  const soldHomePricing =
    membershipPlan && billingOption && membershipSaleEquipment !== undefined
      ? priceMembershipOptionForEquipment({
          option: billingOption,
          coverageRules: membershipPlan.coverageRules,
          equipment: membershipSaleEquipment,
        })
      : null;
  const selectedPlan =
    membershipPlan && billingOption && interval
      ? {
          ...membershipPlan,
          priceAmount: soldHomePricing?.priceAmount ?? billingOption.basePrice,
          billingIntervalValue: interval.billingIntervalValue,
          billingIntervalUnit: interval.billingIntervalUnit,
          contractLengthMonths: billingOption.termMonths || null,
        }
      : membershipPlan;
  const membershipPlanSummary =
    isMembership && selectedPlan
      ? formatMembershipPlanSummary(selectedPlan, {
          complimentaryOption: isComplimentaryBillingOption(billingOption),
        })
      : undefined;
  const persistedBillingOption =
    billingOption &&
    membershipPlan?.billingOptions?.some((option) => option.id === billingOption.id);

  return {
    lineItemId: resolveId(source),
    membershipSetupFee: isMembership ? Number(membershipPlan?.setupFee) || 0 : undefined,
    itemVersion: source.itemVersion ?? 1,
    name: source.name,
    code: source.code,
    // Canonicalize a legacy SERVICE + taxCategory=membership catalog row when
    // it becomes a new document line. The estimate can then identify the sale
    // without depending on the old accounting-category fallback.
    type: isMembership ? 'MEMBERSHIP' : source.type,
    unit: source.unit || 'each',
    description: source.description ?? '',
    basePrice: billingOption
      ? safeNumber(soldHomePricing?.priceAmount ?? billingOption.basePrice)
      : membershipPlan
        ? safeNumber(membershipPlan.priceAmount)
        : safeNumber(source.basePrice),
    quantity: 1,
    taxable: source.taxable ?? true,
    pricebookTaxable: source.taxable === false ? false : undefined,
    priceStrategy: source.priceStrategy ?? 'FLAT',
    taxLineType: source.taxLineType ?? null,
    // Snapshotted, not re-derived. The catalog is where an item's tax class and
    // its accounts are decided, and a document line that arrives without them
    // falls back to a class guessed from its bare type and posts to nothing.
    taxCategory: source.taxCategory ?? null,
    taxCode: source.taxCode ?? null,
    glAccount: source.glAccount ?? null,
    glCogs: source.glCogs ?? null,
    glExpenseAccount: source.glExpenseAccount ?? null,
    glClass: source.glClass ?? null,
    laborRate: safeNumber(source.laborRate),
    laborCostRate: safeNumber(source.laborCostRate),
    costBasis: safeNumber(source.costBasis),
    markupPercent: safeNumber(source.markupPercent),
    // Whether the line starts billed is a question about the SALE, so the type
    // answers it and the catalog never does — the same part is included in one
    // sale and charged in the next. Only where the surface offers the tick to
    // undo it (`partsStartUnbilled`); everywhere else a picked line bills, as
    // it always has.
    chargeable: partsStartUnbilled ? defaultChargeableForType(source.type) : true,
    // ...unless the SERVICE↔PART relationship it arrived through says
    // otherwise. Null when it does not, leaving the line's own flag to answer.
    pricingTreatment: source.pricingTreatment ?? null,
    markupOverride: source.markupOverride ?? null,
    // NOT-605 — carry the item's member pricing so the estimate preview matches
    // the backend. null (not 0) when unset, so "no override" ≠ "free".
    memberPrice: source.memberPrice != null ? safeNumber(source.memberPrice) : null,
    memberDiscountPercent:
      source.memberDiscountPercent != null ? safeNumber(source.memberDiscountPercent) : null,
    // The catalog's default hours, carried onto the line the same way its
    // default parts are. On a flat service they are cost; on a dynamic one the
    // materialized labor lines overwrite them below.
    estimatedHours: safeNumber(source.estimatedHours),
    ...(source.laborCostLines?.length ? { laborCostLines: source.laborCostLines } : {}),
    dynamicPricingConfig: source.dynamicPricingConfig,
    displayConfig: normalizeDisplayConfig(source.displayConfig),
    membershipPlanId: isMembership ? membershipPlanIdForItem(source) : undefined,
    // Scalar-only legacy plans get a browser-only option so they can reuse the
    // picker UI. Only send an id that actually exists on the persisted plan;
    // the string `legacy-default` can itself become a real authored id after a
    // plan is converted.
    ...(billingOption?.id && persistedBillingOption
      ? { membershipBillingOptionId: billingOption.id }
      : {}),
    ...(isMembership && customerChoosesBillingOption
      ? {
          membershipCustomerChoosesBillingOption: true,
          // The customer cannot meaningfully choose a billing option on a
          // membership that the estimate already requires. Delegating the
          // cadence therefore also delegates whether to buy it.
          isOptional: true,
          isSelected: false,
        }
      : {}),
    membershipPlanSummary,
    // Presence is meaningful even when the array is empty: omission asks the
    // server to auto-select every matching unit at the address, while [] means
    // the author deliberately selected none.
    ...(soldHomePricing ? { equipmentIds: soldHomePricing.equipmentIds } : {}),
    ...(isMembership && membershipDeliveredServiceIds.length > 0
      ? { membershipDeliveredServiceIds }
      : {}),
    configurationName,
    imageUrl: source.imageUrl,
    imagePreviewUrl: source.imagePreviewUrl,
  };
}

// Custom line items can only be simple billable items. Memberships need plan
// linkage and a bundle is defined by the catalog items it contains, so a row
// typed in by hand can be neither.
const CUSTOM_ITEM_NEVER_ALLOWED = new Set<string>(['MEMBERSHIP', 'BUNDLE']);
const CUSTOM_ITEM_DEFAULT_TYPES = ['SERVICE', 'MATERIAL', 'EQUIPMENT'] as const;

function resolveCustomItemTypes(allowedTypes?: string[]): string[] {
  const requested = allowedTypes?.length
    ? allowedTypes.map((type) => type.toUpperCase())
    : [...CUSTOM_ITEM_DEFAULT_TYPES];

  return Array.from(new Set(requested)).filter((type) => !CUSTOM_ITEM_NEVER_ALLOWED.has(type));
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LineItemPicker(props: LineItemPickerProps) {
  const pricingHidden = useIsFieldHidden('PRICING');

  // A masked catalog response cannot safely populate a billable row: adding it
  // would turn stripped prices into zeroes. Hide the action consistently across
  // jobs, estimates, invoices, templates, and change orders.
  if (pricingHidden) return null;
  // A changed scope discards preloaded results and in-flight searches before
  // the user can pick an item from the previous business unit.
  return <LineItemPickerContent key={props.businessUnitId ?? 'all'} {...props} />;
}

function LineItemPickerContent({
  businessUnitId,
  onAdd: emitAdd,
  excludeIds = [],
  placeholder = 'Search pricebook items…',
  filterItem,
  emptyMessage = 'No items found',
  allowedTypes = [],
  allowCustomItems = true,
  hidePrices = false,
  expandComponents = false,
  partsStartUnbilled = false,
  membershipCustomerId,
  membershipServiceAddressId,
  allowDeliveredVisits = false,
  getMembershipPotentialSavings,
  portalContainer,
  membershipEditRequest,
  membershipAddRequest,
  onEditMembership,
}: LineItemPickerProps) {
  // Every line leaves the picker through here, so this is where a catalog
  // service's margin shares become the document's (`freezeMarginShares`).
  //
  // A row naming a profile cannot be frozen before the profiles have loaded:
  // frozen against nothing it keeps only the profile id, and document costing
  // (which is handed no profiles) then pays the share at $0. So such a line
  // WAITS for them, and every line emitted after it waits in turn — a service's
  // parts are emitted right behind it, and must not overtake it. If the
  // profiles cannot be had at all, the service is refused along with its parts
  // rather than added with its crew's share silently missing.
  const laborRateProfilesQuery = useLaborRateProfiles();
  const laborRateProfilesRef = useRef(laborRateProfilesQuery);
  laborRateProfilesRef.current = laborRateProfilesQuery;
  const pendingAddsRef = useRef<Promise<void> | null>(null);
  const refusedLineKeysRef = useRef(new Set<string>());
  const onAdd = useCallback(
    (item: PickedLineItem, meta?: LineItemPickerAddMeta) => {
      const namesProfile = Boolean(
        item.laborCostLines?.some(
          (line) => line.rateSource === 'PROFILE' && line.laborRateProfileId,
        ),
      );
      const emit = (profiles: LaborRateProfile[]) =>
        emitAdd(
          item.laborCostLines?.length
            ? { ...item, laborCostLines: freezeMarginShares(item.laborCostLines, profiles) }
            : item,
          meta,
        );

      const loaded = laborRateProfilesRef.current.data;
      if (!pendingAddsRef.current && (!namesProfile || loaded)) {
        emit(loaded ?? []);
        return;
      }

      const run = async () => {
        if (item.parentLineKey && refusedLineKeysRef.current.has(item.parentLineKey)) return;
        let profiles = laborRateProfilesRef.current.data;
        if (!profiles && namesProfile) {
          profiles = (await laborRateProfilesRef.current.refetch()).data;
          if (!profiles) {
            if (item.lineKey) refusedLineKeysRef.current.add(item.lineKey);
            toast.error(
              `Labor rates could not be loaded, so ${item.name} was not added. Try again.`,
            );
            return;
          }
        }
        emit(profiles ?? []);
      };
      const chained = (pendingAddsRef.current ?? Promise.resolve()).then(run);
      pendingAddsRef.current = chained;
      void chained.finally(() => {
        if (pendingAddsRef.current === chained) pendingAddsRef.current = null;
      });
    },
    [emitAdd],
  );
  const membershipEquipmentQuery = useAllEquipment(
    {
      customerId: membershipCustomerId ?? undefined,
      serviceAddressId: membershipServiceAddressId ?? undefined,
    },
    Boolean(membershipCustomerId && membershipServiceAddressId),
  );
  const membershipSaleEquipment = useMemo(
    () =>
      (membershipEquipmentQuery.data?.items ?? [])
        .filter((unit) => unit.status === 'ACTIVE' || unit.status === 'UNKNOWN')
        .map((unit) => ({
          id: unit.id,
          category: unit.category,
          name: unit.name,
          serialNumber: unit.serialNumber,
        })),
    [membershipEquipmentQuery.data?.items],
  );
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PricebookLineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [expandingId, setExpandingId] = useState<string | null>(null);
  // Configurable EQUIPMENT: when the operator adds a configurable placeholder we
  // open a variant chooser and suspend the add until they pick (or cancel). The
  // resolver bridges the imperative handleAdd flow to the dialog's React state.
  const [configurableState, setConfigurableState] = useState<{
    parentName: string;
    variants: ConfigurableVariant[];
  } | null>(null);
  const [membershipBillingState, setMembershipBillingState] = useState<{
    source: PricebookLineItem;
    plan: MembershipPlan;
    options: MembershipBillingOption[];
    addOns: PricebookLineItem[];
    editingItem?: PickedLineItem;
  } | null>(null);
  const lastMembershipAddRequestRef = useRef<number | null>(null);
  const membershipAddContextRef = useRef({ onAdd, partsStartUnbilled, membershipSaleEquipment });
  membershipAddContextRef.current = { onAdd, partsStartUnbilled, membershipSaleEquipment };
  const membershipRecurringServicesQuery = usePlanRecurringServices(
    membershipBillingState?.plan.id ?? null,
  );
  const knownMembershipServicesRef = useRef<{
    planId: string | null;
    services: Map<string, PlanRecurringService>;
  }>({ planId: null, services: new Map() });
  const membershipPlanId = membershipBillingState?.plan.id ?? null;
  if (knownMembershipServicesRef.current.planId !== membershipPlanId) {
    knownMembershipServicesRef.current = { planId: membershipPlanId, services: new Map() };
  }
  for (const service of membershipRecurringServicesQuery.data ?? []) {
    knownMembershipServicesRef.current.services.set(service.id, service);
  }
  const configurableResolverRef = useRef<((variant: ConfigurableVariant | null) => void) | null>(
    null,
  );
  // Rows the user has expanded to reveal pricing detail — the plan summary for
  // membership items, or the member-pricing breakdown for billable items.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState<number>();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestRef = useRef(0);
  // Two keys, because the two uses read the list differently. Filtering only
  // cares which types are in it, so it can sort for a stable memo key; the
  // custom-item default is the caller's FIRST type, and sorting it turned a
  // section that allows ['SERVICE', 'BUNDLE'] into a bundle by default — a
  // hand-typed line that was neither priced nor edited as the service it is.
  const orderedAllowedTypeKey = allowedTypes.map((type) => type.toUpperCase()).join('|');
  const allowedTypeKey = allowedTypes
    .map((type) => type.toUpperCase())
    .sort()
    .join('|');
  const allowedTypeSet = useMemo(
    () => new Set(allowedTypeKey ? allowedTypeKey.split('|') : []),
    [allowedTypeKey],
  );
  const customItemTypes = useMemo(
    () =>
      resolveCustomItemTypes(orderedAllowedTypeKey ? orderedAllowedTypeKey.split('|') : undefined),
    [orderedAllowedTypeKey],
  );

  const search = useCallback(
    async (text: string) => {
      const requestId = ++searchRequestRef.current;
      const trimmed = text.trim();
      setLoading(true);
      try {
        let items: PricebookLineItem[];
        if (trimmed) {
          // Publish the inexpensive lexical match first. Hybrid search also
          // performs lexical ranking, but waits for the semantic embedding and
          // vector stage before returning; a newly created exact match should
          // not look absent during that wait.
          const lexicalRequest = apiGet<
            PricebookLineItem[] | { results?: PricebookLineItem[]; data?: PricebookLineItem[] }
          >(operationsClient, operationsPricebookEndpoints.list(), {
            params: {
              ...(businessUnitId ? { businessUnitId } : {}),
              q: trimmed,
              limit: 20,
              status: 'ACTIVE',
              sortBy: 'name',
              sortDir: 'asc',
              ...(allowedTypeSet.size === 1 ? { type: Array.from(allowedTypeSet)[0] } : {}),
            },
          }).catch(() => [] as PricebookLineItem[]);
          const hybridRequest = apiPost<
            PricebookLineItem[] | { results?: PricebookLineItem[]; data?: PricebookLineItem[] }
          >(operationsClient, '/pricebook/line-items/search-hybrid', {
            queryText: trimmed,
            limit: 20,
            // A section-specific picker must constrain the candidate pool on
            // the server. Filtering a general top-20 response afterward can
            // hide an exact membership/material/equipment match when other
            // item types occupy the pool first.
            ...(businessUnitId || allowedTypeSet.size === 1
              ? {
                  filters: {
                    ...(businessUnitId ? { businessUnitId } : {}),
                    ...(allowedTypeSet.size === 1 ? { type: Array.from(allowedTypeSet)[0] } : {}),
                  },
                }
              : {}),
            // This picker resolves a concrete variant for configurable items
            // (handleAdd), so it's safe — and necessary — to surface them here.
            includeConfigurable: true,
          }).catch(() => [] as PricebookLineItem[]);
          const lexicalData = await lexicalRequest;
          const lexicalItems = Array.isArray(lexicalData)
            ? lexicalData
            : (lexicalData.results ?? lexicalData.data ?? []);
          const normalizedLexical = lexicalItems
            .map(normalizeSearchResult)
            .filter(
              (item) =>
                allowedTypeSet.size === 0 || allowedTypeSet.has(String(item.type).toUpperCase()),
            );
          if (requestId === searchRequestRef.current && normalizedLexical.length > 0) {
            setResults(normalizedLexical);
            setShowResults(true);
          }

          const hybridData = await hybridRequest;
          const hybridItems = Array.isArray(hybridData)
            ? hybridData
            : (hybridData.results ?? hybridData.data ?? []);
          const hybridIds = new Set(hybridItems.map((item) => resolveId(item)));
          items = [
            ...hybridItems,
            ...lexicalItems.filter((item) => !hybridIds.has(resolveId(item))),
          ];
        } else {
          // Empty query → preload the default active line-item list so the
          // picker shows options on focus without typing (NOT-283). The
          // hybrid-search endpoint rejects empty queryText, so use the list.
          const data = await apiGet<
            PricebookLineItem[] | { results?: PricebookLineItem[]; data?: PricebookLineItem[] }
          >(operationsClient, operationsPricebookEndpoints.list(), {
            params: {
              limit: 20,
              status: 'ACTIVE',
              sortBy: 'name',
              sortDir: 'asc',
              ...(businessUnitId ? { businessUnitId } : {}),
              ...(allowedTypeSet.size === 1 ? { type: Array.from(allowedTypeSet)[0] } : {}),
            },
          });
          items = Array.isArray(data)
            ? data
            : ((data as { results?: PricebookLineItem[]; data?: PricebookLineItem[] }).results ??
              (data as { data?: PricebookLineItem[] }).data ??
              []);
        }
        const normalized = items.map(normalizeSearchResult);
        const typeFiltered = normalized.filter(
          (item) =>
            allowedTypeSet.size === 0 || allowedTypeSet.has(String(item.type).toUpperCase()),
        );
        if (requestId !== searchRequestRef.current) return;
        setResults(typeFiltered);
        // Only auto-open the popover for an active (typed) query. The
        // empty-query preload populates results silently; the focus handler
        // decides when to reveal them.
        if (trimmed) setShowResults(typeFiltered.length > 0);
      } catch {
        if (requestId === searchRequestRef.current) setResults([]);
      } finally {
        if (requestId === searchRequestRef.current) {
          setLoading(false);
          setSearched(true);
        }
      }
    },
    [allowedTypeSet, businessUnitId],
  );

  // Tracks whether the empty-query preload has run so focus fetches once.
  const preloadedRef = useRef(false);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Empty query is handled by the on-focus preload, not the debounce.
    if (!query.trim()) return;
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const updateWidth = () => setContentWidth(node.offsetWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // A configurable item is never excluded by an already-added row: it resolves
  // to one of N configurations, so adding one configuration must not hide the
  // item from the operator who still wants another. Callers build `excludeIds`
  // from line ids, and a configured line carries the configurable parent's id.
  const filtered = useMemo(
    () =>
      results.filter(
        (r) =>
          (r.isConfigurable || !excludeIds.includes(resolveId(r))) &&
          (filterItem ? filterItem(r) : true),
      ),
    [excludeIds, filterItem, results],
  );

  // NOT-54 — surface membership pricing in the Add Item flow, but lazily. This
  // component mounts in 16+ places; fetching active plans on every mount would
  // be an eager network request for data only needed once a billable row is
  // shown. The results list only populates after the user focuses/searches, so
  // gating the query on a visible billable row defers the fetch until it's
  // actually reachable. Once enabled, react-query keeps it cached and shared
  // with the per-row MembershipPricingBreakdown (no per-row refetch), so the
  // breakdown is ready by the time a row can be expanded — no waterfall.
  const hasBillableRow = useMemo(
    () => !hidePrices && filtered.some((item) => toMemberPricingItemType(item.type) != null),
    [filtered, hidePrices],
  );
  const activeMembershipPlansQuery = useMembershipPlans(
    { status: 'ACTIVE' },
    { enabled: hasBillableRow, showErrorToast: false },
  );
  const hasActiveMembershipPlans =
    membershipPlansFromQueryData(activeMembershipPlansQuery.data).length > 0;
  const membershipItemsNeedingDetails = useMemo(
    () =>
      Array.from(
        new Set(
          filtered
            .filter((item) => isMembershipType(item.type) && !membershipPlanIdForItem(item))
            .map(resolveId)
            .filter(Boolean),
        ),
      ),
    [filtered],
  );
  const membershipItemDetailQueries = useQueries({
    queries: membershipItemsNeedingDetails.map((id) => ({
      queryKey: lineItemPickerDetailKey(id),
      queryFn: ({ signal }) =>
        apiGet<PricebookLineItem | { data?: PricebookLineItem }>(
          operationsClient,
          operationsPricebookEndpoints.detail(id),
          { signal },
        ),
      enabled: Boolean(id),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const membershipItemDetailsById = useMemo(() => {
    const map = new Map<string, PricebookLineItem>();
    membershipItemsNeedingDetails.forEach((id, index) => {
      const detail = unwrapLineItemDetail(membershipItemDetailQueries[index]?.data);
      if (detail) map.set(id, normalize(detail));
    });
    return map;
  }, [membershipItemDetailQueries, membershipItemsNeedingDetails]);
  const membershipDetailQueriesById = useMemo(() => {
    const map = new Map<string, (typeof membershipItemDetailQueries)[number]>();
    membershipItemsNeedingDetails.forEach((id, index) => {
      const query = membershipItemDetailQueries[index];
      if (query) map.set(id, query);
    });
    return map;
  }, [membershipItemDetailQueries, membershipItemsNeedingDetails]);
  const membershipPlanIds = useMemo(
    () =>
      Array.from(
        new Set(
          filtered
            .filter((item) => isMembershipType(item.type))
            .map((item) => {
              const id = resolveId(item);
              return membershipPlanIdForItem(membershipItemDetailsById.get(id) ?? item);
            })
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [filtered, membershipItemDetailsById],
  );
  const membershipPlanQueries = useQueries({
    queries: membershipPlanIds.map((id) => ({
      queryKey: membershipPlanKeys.detail(id),
      queryFn: ({ signal }) =>
        apiGet<MembershipPlan | { data?: MembershipPlan }>(
          operationsClient,
          operationsPricebookEndpoints.membershipPlan(id),
          { signal },
        ),
      enabled: Boolean(id),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const membershipPlansById = useMemo(() => {
    const map = new Map<string, MembershipPlan>();
    membershipPlanIds.forEach((id, index) => {
      const plan = unwrapMembershipPlan(membershipPlanQueries[index]?.data);
      if (plan) map.set(id, plan);
    });
    return map;
  }, [membershipPlanIds, membershipPlanQueries]);
  const membershipPlanQueriesById = useMemo(() => {
    const map = new Map<string, (typeof membershipPlanQueries)[number]>();
    membershipPlanIds.forEach((id, index) => {
      const query = membershipPlanQueries[index];
      if (query) map.set(id, query);
    });
    return map;
  }, [membershipPlanIds, membershipPlanQueries]);

  const getResolvedMembershipItem = useCallback(
    (item: PricebookLineItem) => membershipItemDetailsById.get(resolveId(item)) ?? item,
    [membershipItemDetailsById],
  );

  /**
   * NOT-628 — a membership whose plan is no longer active is not sellable, so
   * it must not be offered. Archiving a plan promises "existing members keep
   * their benefits but no new members can join"; the item stayed searchable, so
   * a rep could quote an archived plan, and only the approval — after the
   * customer had signed — refused it.
   *
   * Only hidden once the plan is actually KNOWN to be inactive: while its
   * status is still loading the row stays, so a slow request never blanks the
   * picker.
   */
  const sellable = useMemo(
    () =>
      filtered.filter((item) => {
        if (!isMembershipType(item.type)) return true;
        const planId = membershipPlanIdForItem(getResolvedMembershipItem(item));
        if (!planId) return true;
        const plan = membershipPlansById.get(planId);
        if (!plan?.status) return true;
        return String(plan.status).toUpperCase() === 'ACTIVE';
      }),
    [filtered, getResolvedMembershipItem, membershipPlansById],
  );

  const getMembershipPlanState = useCallback(
    (item: PricebookLineItem) => {
      const id = resolveId(item);
      const resolvedItem = getResolvedMembershipItem(item);
      const planId = membershipPlanIdForItem(resolvedItem);
      const detailQuery = membershipDetailQueriesById.get(id);
      if (!planId) {
        const detailPending =
          detailQuery &&
          !detailQuery.isFetched &&
          (detailQuery.isLoading || detailQuery.isFetching);
        return {
          summary: undefined,
          plan: undefined,
          isLoading: Boolean(detailPending),
          isUnavailable: !detailPending,
        };
      }

      const plan = membershipPlansById.get(planId);
      if (plan) {
        return {
          summary: formatMembershipPlanSummary(plan),
          plan,
          isLoading: false,
          isUnavailable: false,
        };
      }

      const planQuery = membershipPlanQueriesById.get(planId);
      const planPending =
        !planQuery || (!planQuery.isFetched && (planQuery.isLoading || planQuery.isFetching));
      return {
        summary: undefined,
        plan: undefined,
        isLoading: planPending,
        isUnavailable: !planPending,
      };
    },
    [
      getResolvedMembershipItem,
      membershipDetailQueriesById,
      membershipPlanQueriesById,
      membershipPlansById,
    ],
  );

  // Resolve a configurable item's variant ids to active, normalized line items.
  // Prefer a single list call (the endpoint supports an `ids` filter) so N
  // variants cost one request rather than N detail fetches.
  const openConfigurableDialog = useCallback(
    (parentName: string, variants: ConfigurableVariant[]) =>
      new Promise<ConfigurableVariant | null>((resolve) => {
        configurableResolverRef.current = resolve;
        setConfigurableState({ parentName, variants });
      }),
    [],
  );

  const resolveConfigurable = useCallback((variant: ConfigurableVariant | null) => {
    const resolve = configurableResolverRef.current;
    configurableResolverRef.current = null;
    setConfigurableState(null);
    resolve?.(variant);
  }, []);

  const handleAdd = async (item: PricebookLineItem) => {
    const id = resolveId(item);
    let source = normalize(item);
    const cachedMembershipDetail = membershipItemDetailsById.get(id);
    if (cachedMembershipDetail) {
      source = { ...source, ...cachedMembershipDetail, id: cachedMembershipDetail.id || source.id };
    }

    if (item.__requiresDetailFetch && !cachedMembershipDetail) {
      setExpandingId(id);
      try {
        const full = await apiGet<PricebookLineItem>(
          operationsClient,
          operationsPricebookEndpoints.detail(id),
        );
        const normalizedFull = normalize(full);
        source = { ...source, ...normalizedFull, id: normalizedFull.id || source.id };
      } catch {
        // keep existing source
      } finally {
        setExpandingId(null);
      }
    }

    if (isMembershipType(source.type) && membershipCustomerId && membershipServiceAddressId) {
      if (membershipEquipmentQuery.isLoading) {
        toast.info('Loading the home equipment before pricing this membership.');
        return;
      }
      if (membershipEquipmentQuery.isError) {
        toast.error('Home equipment could not be loaded. Try the membership again.');
        return;
      }
    }

    // Search projections can omit long-form fields. Dynamic services are
    // materialized as document snapshots, so always resolve the full record
    // before opening the wizard (description, adjustments, and defaults must
    // survive even when the search row was compact).
    if (isServiceType(source.type) && source.priceStrategy === 'DYNAMIC') {
      setExpandingId(id);
      try {
        const full = await apiGet<PricebookLineItem>(
          operationsClient,
          operationsPricebookEndpoints.detail(id),
        );
        const normalizedFull = normalize(full);
        source = { ...source, ...normalizedFull, id: normalizedFull.id || source.id };
      } catch {
        toast.error('Could not load the complete service definition.');
        return;
      } finally {
        setExpandingId(null);
      }
    }

    // Configurable EQUIPMENT: the placeholder is never added directly — the
    // operator picks one CONFIGURATION of the linked type, and the line is built
    // from this item's base with that option's name + price applied.
    if (source.isConfigurable) {
      setExpandingId(id);
      let configurations = source.configurations ?? [];
      try {
        // The hybrid-search projection carries `isConfigurable` but not the
        // configurations; fetch full detail to obtain them if we don't have them.
        if (configurations.length === 0) {
          const full = await apiGet<PricebookLineItem>(
            operationsClient,
            operationsPricebookEndpoints.detail(id),
          );
          const normalizedFull = normalize(full);
          source = { ...source, ...normalizedFull, id: normalizedFull.id || source.id };
          configurations = source.configurations ?? [];
        }
      } catch {
        toast.error('Could not load configurations for this item.');
        return;
      } finally {
        setExpandingId(null);
      }

      if (configurations.length === 0) {
        toast.error('This configurable item has no configurations.');
        return;
      }

      // Present each configuration as a row; the row id is its index so the
      // dialog can identify the choice (the underlying pricebook item is source).
      const rows: ConfigurableVariant[] = configurations.map((config, i) => ({
        id: String(i),
        name: config.name,
        code: source.code ?? '',
        basePrice: config.price ?? source.basePrice ?? 0,
        unit: source.unit,
      }));

      // Suspend the add until the operator picks a configuration (or cancels).
      const chosen = await openConfigurableDialog(source.name, rows);
      if (chosen) {
        const config = configurations[Number(chosen.id)];
        if (config) {
          // Build the line from the configurable item's base (keeps its id so the
          // line links back to this pricebook item) with the option applied. The
          // chosen configuration's name rides along as `configurationName`: it is
          // what tells the sales APIs this line is a RESOLVED selection rather
          // than an unresolved placeholder, and what keeps two configurations of
          // the same parent from collapsing into one row.
          const configuredSource: PricebookLineItem = {
            ...source,
            name: `${source.name} — ${config.name}`,
            basePrice: config.price ?? source.basePrice,
          };
          onAdd(
            toPickedLineItem(
              configuredSource,
              undefined,
              undefined,
              config.name,
              partsStartUnbilled,
            ),
            {
              source: 'pricebook',
            },
          );
          setQuery('');
          setShowResults(false);
        }
      }
      return;
    }

    // A BUNDLE IS ONE SALE, AND ONE LINE ON THE CUSTOMER'S DOCUMENT.
    //
    // The owner's words: "bundle should be as simple as the selected services
    // inside this, and showing in a single line all. The material and equipment
    // as always should show on their section... In the estimate for the
    // customer they will see only the bundle line, but in our side we will see
    // materials and that stuff just for calculating the costs and profits."
    //
    // So the bundle line carries the whole price and every member lands beside
    // it earning NOTHING:
    //
    //   parts (the bundle's own, and those belonging to a member service) get
    //   `included_in_service`, which both the preview and the pricing engine
    //   price at zero — the same treatment a flat service's parts get, and the
    //   reason the double-charging invariant holds here without a special case;
    //
    //   member SERVICES land at a base price of ZERO. A service always charges
    //   revenue — `chargesRevenue` refuses to let one be non-chargeable, and
    //   rightly, since a service billing nothing is a free job — so the only
    //   honest way to say "its money is already inside the bundle" is for it to
    //   have no price of its own. It keeps its hours, which is what it costs.
    //
    // Every member is hidden from the customer's document and every member
    // hangs off the BUNDLE rather than off a member service, so the whole thing
    // is ONE rolled-up group: one taxable base, taxed on what is in it — a
    // service anywhere makes it labor, otherwise materials, which is what a
    // bundle was taxed as when it was a single line with no members at all.
    if (expandComponents && isBundleType(source.type)) {
      setExpandingId(id);
      let members: PricebookLineItem[] | undefined;
      try {
        members = await fetchComponents(id);
        // A member service's own parts are what the operator is costing the
        // bundle from, so they come too. One level: a bundle cannot contain a
        // bundle, and a service cannot contain a service, so this terminates.
        const serviceMembers = members.filter((member) => isServiceType(member.type));
        const nested = await Promise.all(serviceMembers.map((m) => fetchComponents(m.id)));
        // A nested part's quantity is stated PER parent service — one filter
        // per tune-up — so it has to be multiplied through by how many of that
        // service the bundle holds. Flattening without it quotes two tune-ups
        // as consuming one filter, and the bundle's cost and margin are wrong
        // by the difference for every member service with `quantity > 1`.
        members = [
          ...members,
          ...nested.flatMap((parts, index) => {
            const parentQuantity = memberQuantity(serviceMembers[index]);
            return parts.map((part) => ({
              ...part,
              quantity: memberQuantity(part) * parentQuantity,
            }));
          }),
        ];
      } catch {
        // The bundle is still worth adding at its own price; what cannot be
        // shown is the breakdown behind it, and saying so is better than
        // silently quoting a bundle with no visible cost.
        toast.error('Could not load what this bundle contains — added without its breakdown.');
        members = undefined;
      } finally {
        setExpandingId(null);
      }

      if (members?.length) {
        const lineKey = createLineKey();
        const separatelyCharged = members.reduce((sum, member) => {
          if (!isPartType(member.type) || member.pricingTreatment !== 'separate_charge') {
            return sum;
          }
          return sum + safeNumber(member.basePrice) * memberQuantity(member);
        }, 0);
        const pickedBundle: PickedLineItem = {
          ...toPickedLineItem({
            ...source,
            // A COMPONENT_SUM bundle stores the whole offer price. Direct
            // billed-on-top members move their share onto their own lines when
            // materialized, so the parent receives the remainder. A manually
            // priced bundle stores only its own line price and needs no split.
            ...(String(source.priceStrategy).toUpperCase() === 'COMPONENT_SUM'
              ? { basePrice: Math.max(0, safeNumber(source.basePrice) - separatelyCharged) }
              : {}),
          }),
          lineKey,
          // Included members fold into this line. A direct part explicitly
          // billed on top remains a separate customer line.
          componentDisplayMode: 'ROLLED_UP',
        };
        const pickedMembers = members.map((member) => {
          const quantity = memberQuantity(member);
          const isOptional = Boolean((member as { isOptional?: boolean }).isOptional);
          const picked = toPickedLineItem(normalize(member));
          const billedOnTop =
            isPartType(member.type) && member.pricingTreatment === 'separate_charge';
          // Lifted out of the object because the visibility line below has to
          // read it — what a member charges is what decides whether a hidden
          // flag on it means anything.
          const memberPricing = isPartType(member.type)
            ? {
                pricingTreatment: billedOnTop
                  ? ('separate_charge' as const)
                  : ('included_in_service' as const),
              }
            : // A member service's price is inside the bundle's. Its HOURS
              // are not — the work still takes them and they still cost —
              // so they ride along and land in the margin panel's Labor.
              { basePrice: 0, priceStrategy: 'FLAT', dynamicPricingConfig: undefined };
          return {
            ...picked,
            ...memberPricing,
            // Never on the customer's document, whatever the catalog says
            // about showing this item elsewhere. This is the whole point of a
            // bundle: they are buying one thing.
            //
            // Not stated on a MATERIAL member, which the bundle price already
            // covers and which therefore never prints however this reads. It
            // would say nothing today and something nobody meant later — see
            // `canHoldVisibilityDecision`. A member pulled out of the bundle
            // price and billed on its own is a charge the customer is owed a
            // line for, which is what an unstated flag leaves them with.
            ...(billedOnTop
              ? { hiddenFromCustomer: false }
              : String(picked.type).toUpperCase() !== 'MATERIAL'
                ? { hiddenFromCustomer: true }
                : {}),
            lineKey: createLineKey(),
            parentLineKey: lineKey,
            quantity,
            isOptional,
            isSelected: !isOptional,
          } satisfies PickedLineItem;
        });
        onAdd(pickedBundle, { source: 'pricebook' });
        pickedMembers.forEach((member) => onAdd(member, { source: 'pricebook' }));
        setQuery('');
        setShowResults(false);
        return;
      }
      // No members, or none could be loaded — fall through and add the bundle
      // line on its own, exactly as it landed before any of this existed.
    }

    // THE CATALOG ROWS FOR A SERVICE'S PARTS, fetched once.
    //
    // They are the only source of a part's TYPE. The pricing preview's breakdown
    // does not carry it, and the type is what decides whether an uplift charged
    // on equipment alone reaches a given part: `rollUpService` reads anything
    // that is not EQUIPMENT as a material. A snapshot written without them
    // therefore reprices an equipment part on the materials base — silently, and
    // only once someone edits the configuration.
    //
    // Fetched for a dynamic service even when its components are not being
    // expanded onto the document, because the snapshot is persisted on both
    // paths. A FLAT service that cannot list its parts is still worth adding,
    // just without the expansion; a DYNAMIC one is not — see below.
    let serviceComponents: PricebookLineItem[] | undefined;
    let componentsUnavailable = false;
    if (isServiceType(source.type) && (expandComponents || source.dynamicPricingConfig?.enabled)) {
      setExpandingId(id);
      try {
        // The endpoint returns { data, warnings }, not a bare array (mirrors
        // unwrapMembershipPlan below).
        const rawComponents = await apiGet<PricebookLineItem[] | { data?: PricebookLineItem[] }>(
          operationsClient,
          operationsPricebookEndpoints.components(id),
        );
        serviceComponents = Array.isArray(rawComponents)
          ? rawComponents
          : (rawComponents?.data ?? []);
      } catch {
        componentsUnavailable = true;
      } finally {
        setExpandingId(null);
      }
    }

    // A dynamic service is materialized at selection time. The resolved labor
    // tiers are copied onto the document line, so later profile edits affect
    // the pricebook template but never rewrite an existing estimate/job.
    let dynamicPreview: DynamicPriceBreakdown | undefined;
    if (
      isServiceType(source.type) &&
      source.priceStrategy === 'DYNAMIC' &&
      source.dynamicPricingConfig?.enabled
    ) {
      setExpandingId(id);
      try {
        dynamicPreview = await apiPost<DynamicPriceBreakdown>(
          operationsClient,
          `/pricebook/pricing/services/${id}/preview`,
          { afterHours: false },
        );
        source = {
          ...source,
          basePrice: dynamicPreview.calculatedPrice,
          dynamicPricingConfig: {
            ...source.dynamicPricingConfig,
            materializedFixedAdjustment:
              dynamicPreview.afterHoursAdjustment - dynamicPreview.serviceFeeCredit,
            // What the calculated price is made of, frozen at the catalog's
            // figures. The document's part lines are the operator's to edit and
            // stop describing this service the moment they do — see
            // `materializedParts`.
            //
            // The part's TYPE is not on the preview's breakdown, so it comes
            // from the components fetched above — on THIS path too, not only on
            // the expanding one. Without it an uplift charged on equipment alone
            // would be charged on the wrong base when the configuration is
            // edited, and nothing would say so.
            materializedParts: materializedPartsFrom(dynamicPreview, serviceComponents),
            laborLines: (source.dynamicPricingConfig.laborLines ?? []).map((line, index) => ({
              ...line,
              resolvedTiers: dynamicPreview!.laborLines[index]?.tiers ?? [],
              resolvedRateName: dynamicPreview!.laborLines[index]?.source,
            })),
          },
        };
      } catch {
        toast.error('Could not calculate the current service price.');
        return;
      } finally {
        setExpandingId(null);
      }
    }

    // A DYNAMIC service whose parts cannot be TYPED is not materialized at all.
    //
    // The type lives only on the catalog rows, and `rollUpService` reads
    // anything that is not EQUIPMENT as a material — so an uplift charged on
    // equipment alone would reprice on the materials base, silently, and only
    // once someone edited the configuration. Both records we could write instead
    // are wrong: a typeless snapshot mis-buckets that uplift, and writing none
    // sends the next reprice to document part lines this path never created,
    // losing every part's contribution outright.
    //
    // So it refuses, exactly as a failed price preview already does directly
    // above. A dynamic service that cannot be priced correctly does not go onto
    // the document. Only when the catalog says it HAS parts — a labor-only
    // service snapshots `[]` and has no type to be missing. A FLAT service is
    // untouched by this: its price is the operator's number, not a calculation
    // over typed parts, so it still adds unexpanded.
    if (dynamicPreview && componentsUnavailable && dynamicPreview.components?.length) {
      toast.error('Could not load this service’s parts.');
      return;
    }

    // NOT-590 — a SERVICE that includes materials/equipment becomes the service
    // line PLUS one real, priced line per component. They are ordinary lines, so
    // pricing, tax, margin and GL need no bundle-shaped special case; only the
    // CUSTOMER's view varies, via `componentDisplayMode`.
    //
    // Opt-in per surface: document editors that hold a list of lines want the
    // expansion, but a picker used to choose ONE item (a production kit's
    // service, a job type's default) must keep returning exactly one line.
    if (expandComponents && isServiceType(source.type)) {
      const components = serviceComponents ?? [];

      if (components.length > 0) {
        const lineKey = createLineKey();
        const fullDynamicPrice = dynamicPreview?.calculatedPrice ?? 0;
        const parentDynamicPrice = Math.max(
          0,
          fullDynamicPrice - (dynamicPreview?.partsSeparatelyCharged ?? 0),
        );
        const pickedService: PickedLineItem = {
          ...toPickedLineItem({
            ...source,
            // The catalog preview is the whole OFFER total. A component that
            // bills on its own line carries part of that total, so the parent
            // service line receives only the remainder. The lines then add
            // back to the exact catalog total, with no double charge.
            //
            ...(dynamicPreview && {
              basePrice: parentDynamicPrice,
              memberPrice: restatedMemberPrice(
                source.memberPrice,
                fullDynamicPrice,
                parentDynamicPrice,
              ),
              dynamicPricingConfig: {
                ...source.dynamicPricingConfig!,
                // Same snapshot, now able to say which of its parts is
                // equipment: the components fetch is the only source of the
                // type, and this is the path that has made it.
                materializedParts: materializedPartsFrom(dynamicPreview, components),
              },
            }),
          }),
          lineKey,
          // The document model still bills T&M service time from
          // `estimatedHours`, and the server rejects a T&M line whose value
          // is 0. A dynamic service keeps its time in `laborLines`, so carry
          // that total across — otherwise the materialized service cannot be
          // saved at all.
          ...(dynamicPreview && { estimatedHours: dynamicPreview.totalHours }),
          // Match the catalog column's default: a catalog item that carries
          // no mode is a bundle, and falling back the other way would tax
          // its parts separately the moment it lands on a document.
          componentDisplayMode: 'ROLLED_UP',
        };
        const pickedComponents = components.map((component) => {
          // PER SERVICE in the catalog, ABSOLUTE on the document — two installs
          // that each consume two copper fittings is a line of four. The same
          // multiplication the bundle path does when it flattens a member
          // service's parts, and the same one `expandComponentLines` does in
          // operations. A service picked here always lands at one, so this
          // changes no number today; it is written out because the invariant
          // belongs where the line is MADE and not only where it is edited, and
          // a picker that one day adds two of something would otherwise quote
          // them as consuming one part (NOT-633).
          const quantity = memberQuantity(component) * pickedService.quantity;
          const isOptional = Boolean((component as { isOptional?: boolean }).isOptional);
          const picked = toPickedLineItem(normalize(component));
          // How the SERVICE prices decides what its parts are: a flat
          // service already charges for them, so they land cost-only and
          // the document totals the flat price rather than the price plus
          // the parts again. Resolved once, HERE, and saved on the line —
          // a rule applied at read time would change what documents already
          // sent to customers charge. The relationship's own answer, where
          // the pricebook gave one, still wins.
          //
          // Lifted out of the object because the visibility snapshot below
          // has to read it: whether this part charges is what decides
          // whether the pricebook's eye said anything.
          const pricingTreatment = partPricingTreatmentForService(picked, source);
          return {
            ...picked,
            pricingTreatment,
            // The service's visibility choice is meaningful only for
            // zero-priced equipment. Priced parts must print to explain their
            // amount, and zero-priced materials are internal consumable scope.
            ...((component as { showInDetail?: boolean | null }).showInDetail === false &&
              canHoldVisibilityDecision({ ...picked, pricingTreatment }) && {
                hiddenFromCustomer: true,
              }),
            lineKey: createLineKey(),
            parentLineKey: lineKey,
            quantity,
            // An optional component is offered, not sold: it lands as a
            // line the operator can tick on, not one already billed.
            isOptional,
            isSelected: !isOptional,
          } satisfies PickedLineItem;
        });
        // Added like any other service. A dynamic service used to open a
        // setup wizard first; there is nothing left for it to ask, because
        // the price is calculated and the parts are cost-only either way.
        onAdd(pickedService, { source: 'pricebook' });
        pickedComponents.forEach((component) => onAdd(component, { source: 'pricebook' }));
        setQuery('');
        setShowResults(false);
        return;
      }
      // No components — fall through and add the service line on its own.
    }

    if (dynamicPreview) {
      // The hours are carried because the server rejects a dynamic line quoting
      // none, and a dynamic service keeps its time in `laborLines`.
      onAdd(
        { ...toPickedLineItem(source), estimatedHours: dynamicPreview.totalHours },
        { source: 'pricebook' },
      );
      setQuery('');
      setShowResults(false);
      return;
    }

    let membershipPlan: MembershipPlan | undefined;
    if (isMembershipType(source.type)) {
      const planId = membershipPlanIdForItem(source);
      membershipPlan = planId ? membershipPlansById.get(planId) : undefined;
      if (planId && !membershipPlan) {
        setExpandingId(id);
        try {
          const rawPlan = await apiGet<MembershipPlan | { data?: MembershipPlan }>(
            operationsClient,
            operationsPricebookEndpoints.membershipPlan(planId),
          );
          membershipPlan = unwrapMembershipPlan(rawPlan);
        } catch {
          // keep adding the membership item without a plan summary
        } finally {
          setExpandingId(null);
        }
      }
    }

    if (membershipPlan && isMembershipType(source.type)) {
      const options = effectiveBillingOptions(membershipPlan);
      const addOnIds = Array.from(
        new Set(
          (source.upsellItems ?? [])
            .filter((item) => item.relationshipType !== 'alternative')
            .map((item) => item.itemId)
            .filter((itemId): itemId is string => Boolean(itemId)),
        ),
      );
      let addOns: PricebookLineItem[] = [];
      if (addOnIds.length > 0) {
        setExpandingId(id);
        try {
          addOns = (
            await Promise.all(
              addOnIds.map((addOnId) =>
                apiGet<PricebookLineItem>(
                  operationsClient,
                  operationsPricebookEndpoints.detail(addOnId),
                ),
              ),
            )
          )
            .map(normalize)
            .filter(
              (addOn) =>
                !isMembershipType(addOn.type) &&
                !isBundleType(addOn.type) &&
                String(addOn.status ?? 'ACTIVE').toUpperCase() === 'ACTIVE',
            );
        } catch {
          toast.error('Could not load this membership’s optional add-ons.');
          return;
        } finally {
          setExpandingId(null);
        }
      }
      // A one-option, no-equipment plan still promises visits, and the tech may
      // have just performed one. Skipping the dialog is what made that question
      // unanswerable for the simplest plans of all.
      let promisesVisits = false;
      if (
        allowDeliveredVisits &&
        membershipPlan.id &&
        options.length === 1 &&
        (membershipPlan.coverageRules?.length ?? 0) === 0 &&
        addOns.length === 0
      ) {
        try {
          const services = await apiGet<{ data: unknown[] }>(
            operationsClient,
            operationsPricebookEndpoints.planRecurringServices(membershipPlan.id!),
          );
          promisesVisits = (services.data ?? []).length > 0;
        } catch {
          promisesVisits = false;
        }
      }

      if (
        options.length > 1 ||
        (membershipPlan.coverageRules?.length ?? 0) > 0 ||
        addOns.length > 0 ||
        promisesVisits
      ) {
        setMembershipBillingState({ source, plan: membershipPlan, options, addOns });
        setQuery('');
        setShowResults(false);
        return;
      }
      onAdd(
        toPickedLineItem(
          source,
          membershipPlan,
          options[0],
          undefined,
          partsStartUnbilled,
          membershipSaleEquipment,
        ),
        {
          source: 'pricebook',
        },
      );
      setQuery('');
      setShowResults(false);
      return;
    }

    onAdd(toPickedLineItem(source, membershipPlan, undefined, undefined, partsStartUnbilled), {
      source: 'pricebook',
    });
    setQuery('');
    setShowResults(false);
  };

  useEffect(() => {
    if (
      !membershipAddRequest ||
      lastMembershipAddRequestRef.current === membershipAddRequest.requestId
    ) {
      return;
    }
    lastMembershipAddRequestRef.current = membershipAddRequest.requestId;

    let cancelled = false;
    const openRecommendedMembership = async () => {
      try {
        const rawItems = await apiGet<
          | PricebookLineItem[]
          | {
              data?: PricebookLineItem[];
              items?: PricebookLineItem[];
              results?: PricebookLineItem[];
            }
        >(operationsClient, operationsPricebookEndpoints.list(), {
          params: {
            membershipPlanId: membershipAddRequest.planId,
            status: 'ACTIVE',
            limit: 1,
          },
        });
        const candidates = Array.isArray(rawItems)
          ? rawItems
          : (rawItems.items ?? rawItems.data ?? rawItems.results ?? []);
        const listedSource = candidates[0];
        if (!listedSource) throw new Error('Membership pricebook item is missing');

        const [rawSource, rawPlan] = await Promise.all([
          apiGet<PricebookLineItem | { data?: PricebookLineItem }>(
            operationsClient,
            operationsPricebookEndpoints.detail(resolveId(listedSource)),
          ),
          apiGet<MembershipPlan | { data?: MembershipPlan }>(
            operationsClient,
            operationsPricebookEndpoints.membershipPlan(membershipAddRequest.planId),
          ),
        ]);
        const sourceDetail = unwrapLineItemDetail(rawSource);
        const plan = unwrapMembershipPlan(rawPlan);
        if (!sourceDetail || !plan || cancelled) return;
        const source = normalize(sourceDetail);

        const addOnIds = Array.from(
          new Set(
            (source.upsellItems ?? [])
              .filter((item) => item.relationshipType !== 'alternative')
              .map((item) => item.itemId)
              .filter((itemId): itemId is string => Boolean(itemId)),
          ),
        );
        const addOns = (
          await Promise.all(
            addOnIds.map((addOnId) =>
              apiGet<PricebookLineItem | { data?: PricebookLineItem }>(
                operationsClient,
                operationsPricebookEndpoints.detail(addOnId),
              ),
            ),
          )
        )
          .map(unwrapLineItemDetail)
          .filter((addOn): addOn is PricebookLineItem => Boolean(addOn))
          .map(normalize)
          .filter(
            (addOn) =>
              !isMembershipType(addOn.type) &&
              !isBundleType(addOn.type) &&
              String(addOn.status ?? 'ACTIVE').toUpperCase() === 'ACTIVE',
          );
        if (cancelled) return;
        const options = effectiveBillingOptions(plan);
        // Legacy plans store one scalar price instead of authored billing
        // options. The normal pricebook picker adds those plans directly;
        // opening the configurator here would render an empty billing step
        // whose confirmation can never become enabled.
        if (options.length === 0) {
          const addContext = membershipAddContextRef.current;
          addContext.onAdd(
            toPickedLineItem(
              source,
              plan,
              undefined,
              undefined,
              addContext.partsStartUnbilled,
              addContext.membershipSaleEquipment,
            ),
            { source: 'pricebook' },
          );
          return;
        }
        setMembershipBillingState({
          source,
          plan,
          options,
          addOns,
        });
      } catch {
        if (!cancelled) toast.error('Could not open this membership offer.');
      }
    };

    void openRecommendedMembership();
    return () => {
      cancelled = true;
    };
  }, [membershipAddRequest]);

  useEffect(() => {
    if (!membershipEditRequest) return;

    let cancelled = false;
    const openExistingMembership = async () => {
      const existing = membershipEditRequest.item;
      try {
        const rawSource = await apiGet<PricebookLineItem>(
          operationsClient,
          operationsPricebookEndpoints.detail(existing.lineItemId),
        );
        const source = normalize(rawSource);
        const planId = existing.membershipPlanId ?? membershipPlanIdForItem(source);
        if (!planId) throw new Error('Membership plan link is missing');
        const rawPlan = await apiGet<MembershipPlan | { data?: MembershipPlan }>(
          operationsClient,
          operationsPricebookEndpoints.membershipPlan(planId),
        );
        const plan = unwrapMembershipPlan(rawPlan);
        if (!plan) throw new Error('Membership plan could not be loaded');
        if (cancelled) return;

        // Add-ons become ordinary estimate lines after selection. They remain
        // editable on the estimate itself; omitting them here prevents reopening
        // the membership from silently adding the same add-on a second time.
        setMembershipBillingState({
          source,
          plan,
          options: effectiveBillingOptions(plan),
          addOns: [],
          editingItem: existing,
        });
      } catch {
        if (!cancelled) toast.error('Could not reopen this membership configuration.');
      }
    };

    void openExistingMembership();
    return () => {
      cancelled = true;
    };
  }, [membershipEditRequest]);

  const handleCustomItem = () => {
    const defaultType = customItemTypes[0] ?? 'SERVICE';

    onAdd(
      {
        lineItemId: createUuid(),
        itemVersion: 1,
        name: '',
        code: '',
        type: defaultType,
        unit: 'each',
        description: '',
        basePrice: 0,
        quantity: 1,
        taxable: true,
        priceStrategy: 'FLAT',
        // Nothing is asserted about a hand-typed line's tax class or its
        // accounts yet: there is no catalog row behind it to snapshot, and the
        // operator states them on the line itself. Null, never a guess — an
        // absent tax line type is what lets the engine derive one from the
        // type, and an invented account would post real money to the wrong
        // place.
        taxLineType: null,
        taxCategory: null,
        taxCode: null,
        glAccount: null,
        glCogs: null,
        glExpenseAccount: null,
        glClass: null,
        laborRate: 0,
        laborCostRate: 0,
        costBasis: 0,
        markupPercent: 0,
        // Same answer as a picked line, from the same type — a hand-typed
        // material in the Materials section is the operator adding a material,
        // and the row's type is the only thing that has been said about it yet.
        chargeable: partsStartUnbilled ? defaultChargeableForType(defaultType) : true,
        estimatedHours: 0,
        isCustom: true,
      },
      { source: 'custom' },
    );
    setQuery('');
    setShowResults(false);
  };

  const showEmptyState = searched && !loading && sellable.length === 0 && query.trim();
  const popoverOpen = showResults && (sellable.length > 0 || Boolean(showEmptyState));

  // Preload pricebook items on focus (NOT-283): show options immediately
  // without typing. Runs the empty-query list fetch once, then reuses it.
  const handleFocus = useCallback(() => {
    if (query.trim()) {
      if (results.length > 0 || showEmptyState) setShowResults(true);
      return;
    }
    if (preloadedRef.current) {
      if (results.length > 0) setShowResults(true);
      return;
    }
    preloadedRef.current = true;
    void search('').then(() => setShowResults(true));
  }, [query, results.length, showEmptyState, search]);

  function toggleExpanded(itemId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  return (
    <>
      <div className="flex gap-2">
        <Popover
          open={popoverOpen}
          onOpenChange={(open) => {
            if (!open) setShowResults(false);
          }}
        >
          <PopoverAnchor asChild>
            <div ref={containerRef} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={placeholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={handleFocus}
                className="pl-9"
              />
              {(loading || expandingId) && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>
          </PopoverAnchor>

          <PopoverContent
            align="start"
            container={portalContainer ?? undefined}
            className="max-h-64 overflow-auto p-0"
            style={{ width: contentWidth }}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={(event) => {
              const target = event.detail.originalEvent.target;
              if (target instanceof Node && containerRef.current?.contains(target)) {
                event.preventDefault();
              }
            }}
          >
            {sellable.length > 0 ? (
              sellable.map((item) => {
                const id = resolveId(item);
                const isMembership = isMembershipType(item.type);
                const membershipState = isMembership ? getMembershipPlanState(item) : null;
                const membershipSummary = membershipState?.summary;
                const canExpandMembership =
                  !hidePrices &&
                  Boolean(
                    membershipSummary &&
                    (membershipSummary.coveredServices.length > 0 ||
                      membershipSummary.contractTerm ||
                      membershipSummary.valuePoints.length > 0),
                  );
                // NOT-54 — billable (non-membership) items can expand to reveal
                // what each active membership plan charges for them.
                const memberPricingType = toMemberPricingItemType(item.type);
                const canShowMemberPricing =
                  !hidePrices && hasActiveMembershipPlans && memberPricingType != null;
                const isExpanded = expandedIds.has(id);
                const canExpand = canExpandMembership || canShowMemberPricing;
                const secondaryText = isMembership
                  ? hidePrices
                    ? 'Membership'
                    : membershipSummary
                      ? `Membership · ${membershipSummary.short}`
                      : membershipState?.isLoading
                        ? 'Membership · loading pricing...'
                        : 'Membership · pricing not available'
                  : hidePrices
                    ? item.type
                    : `${item.type} · ${catalogDisplayPriceText(item)}`;

                return (
                  <div key={id} className="border-b last:border-b-0">
                    <div className="flex w-full items-stretch transition-colors hover:bg-accent">
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left"
                        onClick={() => handleAdd(item)}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{item.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{secondaryText}</p>
                        </div>
                      </button>
                      {canExpand ? (
                        <button
                          type="button"
                          className="flex w-8 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                          title={
                            isMembership
                              ? isExpanded
                                ? 'Collapse membership details'
                                : 'Expand membership details'
                              : isExpanded
                                ? 'Collapse member pricing'
                                : 'Expand member pricing'
                          }
                          aria-expanded={isExpanded}
                          onClick={() => toggleExpanded(id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="flex w-9 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
                        aria-label={`Add ${item.name}`}
                        onClick={() => handleAdd(item)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {isMembership && membershipSummary && isExpanded && !hidePrices ? (
                      <div className="space-y-1 border-t bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                        <p>
                          {[
                            membershipSummary.recurring,
                            membershipSummary.discount,
                            membershipSummary.contractTerm,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        {membershipSummary.coveredServices.length > 0 ? (
                          <ul className="space-y-0.5">
                            {membershipSummary.coveredServices.map((service, index) => (
                              <li key={`${service.name}-${index}`}>
                                {service.name} - {service.quantityPerPeriod} {service.period}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {membershipSummary.valuePoints.length > 0 ? (
                          <ul className="list-disc space-y-0.5 pl-4">
                            {membershipSummary.valuePoints.slice(0, 3).map((point, index) => (
                              <li key={`vp-${index}`}>{point}</li>
                            ))}
                            {membershipSummary.valuePoints.length > 3 ? (
                              <li className="list-none text-muted-foreground/70">
                                +{membershipSummary.valuePoints.length - 3} more
                              </li>
                            ) : null}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}
                    {canShowMemberPricing && memberPricingType && isExpanded ? (
                      <div className="space-y-1.5 border-t bg-muted/30 px-3 py-2">
                        <p className="text-xs font-medium text-muted-foreground">Member pricing</p>
                        <MembershipPricingBreakdown
                          basePrice={catalogDisplayPrice(item)}
                          itemType={memberPricingType}
                          itemId={id}
                          memberPrice={item.memberPrice != null ? Number(item.memberPrice) : null}
                          memberDiscountPercent={
                            item.memberDiscountPercent != null
                              ? Number(item.memberDiscountPercent)
                              : null
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <div className="p-3 text-center text-sm text-muted-foreground">{emptyMessage}</div>
            )}
          </PopoverContent>
        </Popover>

        {allowCustomItems && customItemTypes.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5 text-xs"
            onClick={handleCustomItem}
          >
            <Plus className="h-3.5 w-3.5" />
            Custom item
          </Button>
        ) : null}
      </div>

      <ConfigurableVariantDialog
        open={configurableState != null}
        onOpenChange={(open) => {
          // Any close that isn't an explicit pick (Cancel, overlay, Esc, X)
          // resolves to null so the configurable parent is never added.
          if (!open) resolveConfigurable(null);
        }}
        parentName={configurableState?.parentName ?? ''}
        variants={configurableState?.variants ?? []}
        onSelect={(variant) => resolveConfigurable(variant)}
      />
      <MembershipBillingOptionDialog
        allowDeliveredVisits={allowDeliveredVisits}
        mode={membershipBillingState?.editingItem ? 'edit' : 'add'}
        initialSelection={
          membershipBillingState?.editingItem
            ? {
                billingOptionId: membershipBillingState.editingItem.membershipBillingOptionId,
                customerChoosesBillingOption:
                  membershipBillingState.editingItem.membershipCustomerChoosesBillingOption,
                equipmentIds: membershipBillingState.editingItem.equipmentIds,
                deliveredServiceIds:
                  membershipBillingState.editingItem.membershipDeliveredServiceIds,
              }
            : undefined
        }
        open={membershipBillingState != null}
        onOpenChange={(open) => {
          if (!open) setMembershipBillingState(null);
        }}
        plan={membershipBillingState?.plan ?? null}
        options={membershipBillingState?.options ?? []}
        services={membershipRecurringServicesQuery.data ?? []}
        servicesLoading={membershipRecurringServicesQuery.isLoading}
        servicesError={membershipRecurringServicesQuery.isError}
        equipment={membershipSaleEquipment}
        addOns={(membershipBillingState?.addOns ?? []).map((addOn) => ({
          id: resolveId(addOn),
          name: addOn.name,
          description: addOn.description,
          type: addOn.type,
          basePrice: safeNumber(addOn.basePrice),
          estimatedHours: safeNumber(addOn.estimatedHours),
        }))}
        customerId={membershipCustomerId}
        serviceAddressId={membershipServiceAddressId}
        potentialSavings={
          membershipBillingState
            ? getMembershipPotentialSavings?.(membershipBillingState.plan)
            : undefined
        }
        equipmentState={
          !membershipCustomerId || !membershipServiceAddressId
            ? 'AWAITING_LOCATION'
            : membershipEquipmentQuery.isLoading
              ? 'LOADING'
              : membershipEquipmentQuery.isError
                ? 'ERROR'
                : 'READY'
        }
        onSelect={async (
          option,
          selectedEquipment,
          selectedAddOnIds,
          deliveredServiceIds,
          customerChoosesBillingOption,
          equipmentSelectionResolved,
        ) => {
          const state = membershipBillingState;
          if (!state) return;
          const persistedOption = state.options.find((candidate) => candidate.id === option.id);
          if (!persistedOption) return;
          const committedDeliveredServiceIds = customerChoosesBillingOption
            ? []
            : deliveredServiceIds;
          const picked = toPickedLineItem(
            state.source,
            state.plan,
            persistedOption,
            undefined,
            partsStartUnbilled,
            // Undefined means the home's coverage is not known yet, so the
            // line omits equipmentIds and Operations resolves matching units
            // when the estimate is saved. [] is reserved for a deliberate
            // empty choice made after the equipment query was ready.
            equipmentSelectionResolved ? selectedEquipment : undefined,
            committedDeliveredServiceIds,
            customerChoosesBillingOption,
          );
          if (state.editingItem) {
            onEditMembership?.({
              // An estimate line is a sold snapshot. The catalog item may
              // have been renamed, re-described, re-taxed, or versioned since
              // this row was added, so editing membership configuration must
              // start from the sold row and patch only what this dialog owns.
              ...state.editingItem,
              basePrice: picked.basePrice,
              // The pricebook item can be linked to a different plan after a
              // sale. Editing the sold row must keep its snapshotted plan.
              membershipPlanId: state.editingItem.membershipPlanId ?? state.plan.id,
              membershipBillingOptionId: picked.membershipBillingOptionId,
              membershipPlanSummary: picked.membershipPlanSummary,
              ...(picked.equipmentIds !== undefined ? { equipmentIds: picked.equipmentIds } : {}),
              membershipDeliveredServiceIds: committedDeliveredServiceIds,
              // `toPickedLineItem` omits false-valued optional fields, which is
              // correct for a new row but not for an edit merged over the old
              // row. Send the explicit answer so switching Customer -> Office
              // clears the persisted delegation instead of inheriting `true`.
              membershipCustomerChoosesBillingOption: customerChoosesBillingOption,
              // Optional/required is an estimate decision independent of the
              // office-selected billing cadence. Editing one must preserve the
              // other. Customer choice remains necessarily optional.
              isOptional: customerChoosesBillingOption ? true : state.editingItem.isOptional,
              isSelected: customerChoosesBillingOption ? false : state.editingItem.isSelected,
            });
          } else {
            onAdd(picked, { source: 'pricebook' });
          }
          setMembershipBillingState(null);
          for (const addOn of state.addOns.filter((candidate) =>
            selectedAddOnIds.includes(resolveId(candidate)),
          )) {
            await handleAdd(addOn);
          }
          // A visit the tech performed on this trip belongs on the document the
          // customer signs — at no charge, the way the same visit lands as a $0
          // line when it is booked instead of sold. Without it the estimate
          // shows a membership fee and no sign of the work done for it.
          const deliveredVisits = Array.from(
            knownMembershipServicesRef.current.services.values(),
          ).filter(
            (service) =>
              committedDeliveredServiceIds.includes(service.id) &&
              service.invoiceTemplateItemId &&
              !excludeIds.includes(service.invoiceTemplateItemId),
          );
          for (const visit of deliveredVisits) {
            try {
              const item = await apiGet<PricebookLineItem>(
                operationsClient,
                operationsPricebookEndpoints.detail(visit.invoiceTemplateItemId!),
              );
              const covered = toPickedLineItem(normalize(item));
              onAdd(
                {
                  ...covered,
                  // The catalog price stays on the line; the override is what
                  // makes this one free. Zeroing the basis would leave nothing
                  // to charge if the visit ever has to become billable.
                  manualPriceOverride: 0,
                  description:
                    visit.label?.trim() || covered.description || 'Included visit performed today',
                },
                { source: 'pricebook' },
              );
            } catch {
              toast.error(
                `Added the membership, but could not add the line for ${
                  visit.label?.trim() || 'the visit performed today'
                }.`,
              );
            }
          }
        }}
      />
    </>
  );
}
